import { useTranslation } from 'react-i18next';
import { FuelType } from '@/api/generated/schemas';
import { Checkbox, Field, Input, Panel, Select } from '@/shared/ui';
import { DateField, TextField } from '@/shared/forms';
import { OrganizationSelectorField } from '@/modules/general';
import { DriverSelectorField } from '../../../components/DriverSelector';
import {
  vehicleCategorySelector,
  vehicleGroupSelector,
  vehicleManufacturerSelector,
} from '../../../components/vehicleCodebooks';
import type { VehicleFormApi } from '../useVehicleForm';

const LABEL_W = 168;

const CategoryField = vehicleCategorySelector.Field;
const GroupField = vehicleGroupSelector.Field;
const ManufacturerField = vehicleManufacturerSelector.Field;

interface VehicleGeneralTabProps {
  form: VehicleFormApi;
}

/**
 * Záložka General — panely identita / technika / provoz / expirace / řidiči + disabled zasedací pořádek.
 */
export function VehicleGeneralTab({ form }: VehicleGeneralTabProps) {
  const { t } = useTranslation('inventory');

  const fuelOptions = [
    { value: FuelType.Diesel, label: t('vehicle.fuelType.diesel') },
    { value: FuelType.DieselEuro6, label: t('vehicle.fuelType.dieselEuro6') },
    { value: FuelType.Hybrid, label: t('vehicle.fuelType.hybrid') },
    { value: FuelType.Electric, label: t('vehicle.fuelType.electric') },
    { value: FuelType.Petrol, label: t('vehicle.fuelType.petrol') },
  ];

  return (
    <div className="gap-phx-3 flex flex-col">
      <Panel variant="filled" icon="building" title={t('vehicle.sections.identity')}>
        <div className="gap-phx-3 flex flex-col">
          <form.Field name="plate">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.plate.label')}
                required
                placeholder={t('vehicle.plate.placeholder')}
                data-testid="vehicle-plate"
              />
            )}
          </form.Field>

          <form.Field name="name">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.name.label')}
                required
                placeholder={t('vehicle.name.placeholder')}
                data-testid="vehicle-name"
              />
            )}
          </form.Field>

          <form.Field name="owner">
            {(field) => (
              <OrganizationSelectorField
                field={field}
                mode="single"
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.owner.label')}
                required
                placeholder={t('vehicle.owner.placeholder')}
                testId="vehicle-owner"
              />
            )}
          </form.Field>

          <form.Field name="category">
            {(field) => (
              <CategoryField
                field={field}
                mode="single"
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.category.label')}
                required
                placeholder={t('vehicle.category.placeholder')}
                testId="vehicle-category"
              />
            )}
          </form.Field>

          <form.Field name="group">
            {(field) => (
              <GroupField
                field={field}
                mode="single"
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.group.label')}
                required
                placeholder={t('vehicle.group.placeholder')}
                testId="vehicle-group"
              />
            )}
          </form.Field>

          <form.Field name="manufacturer">
            {(field) => (
              <ManufacturerField
                field={field}
                mode="single"
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.manufacturer.label')}
                required
                placeholder={t('vehicle.manufacturer.placeholder')}
                testId="vehicle-manufacturer"
              />
            )}
          </form.Field>
        </div>
      </Panel>

      <Panel variant="filled" icon="bus" title={t('vehicle.sections.technical')}>
        <div className="gap-phx-3 flex flex-col">
          <form.Field name="passengerSeats">
            {(field) => (
              <TextField
                field={field}
                type="number"
                min={1}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.passengerSeats.label')}
                required
                data-testid="vehicle-passenger-seats"
              />
            )}
          </form.Field>

          <form.Field name="tourLeaderSeats">
            {(field) => (
              <TextField
                field={field}
                type="number"
                min={0}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.tourLeaderSeats.label')}
              />
            )}
          </form.Field>

          <Field layout="inline" labelWidth={LABEL_W} label={t('vehicle.sections.seating')}>
            {/* Zasedací pořádek je vědomě odložený — žádný DB sloupec ani FK. */}
            <Input value={t('vehicle.seatingPlaceholder')} readOnly disabled />
          </Field>

          <form.Field name="yearOfManufacture">
            {(field) => (
              <TextField
                field={field}
                type="number"
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.yearOfManufacture.label')}
              />
            )}
          </form.Field>

          <form.Field name="vin">
            {(field) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.vin.label')}
              />
            )}
          </form.Field>

          <form.Field name="initialMileageKm">
            {(field) => (
              <TextField
                field={field}
                type="number"
                min={0}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.initialMileageKm.label')}
              />
            )}
          </form.Field>

          <form.Field name="fuelType">
            {(field) => (
              <Field layout="inline" labelWidth={LABEL_W} label={t('vehicle.fuelType.label')}>
                <Select
                  value={field.state.value ?? ''}
                  onChange={(event) =>
                    field.handleChange((event.target.value || null) as FuelType | null)
                  }
                  placeholder={t('vehicle.fuelType.placeholder')}
                  options={fuelOptions}
                />
              </Field>
            )}
          </form.Field>
        </div>
      </Panel>

      <Panel variant="filled" icon="calendar" title={t('vehicle.sections.operation')}>
        <div className="gap-phx-3 flex flex-col">
          <form.Field name="commissioningDate">
            {(field) => (
              <DateField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.commissioningDate.label')}
                required
              />
            )}
          </form.Field>

          <form.Field name="retirementDate">
            {(field) => (
              <DateField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.retirementDate.label')}
              />
            )}
          </form.Field>

          <form.Field name="displayOnResSheetWithRidesOnly">
            {(field) => (
              <Field
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.displayOnResSheetWithRidesOnly.label')}
              >
                <Checkbox
                  checked={field.state.value}
                  onCheckedChange={(checked) => field.handleChange(checked === true)}
                />
              </Field>
            )}
          </form.Field>
        </div>
      </Panel>

      <Panel variant="filled" icon="alert" title={t('vehicle.sections.expiry')}>
        <div className="gap-phx-3 flex flex-col">
          <form.Field name="insuranceExpiry">
            {(field) => (
              <DateField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.insuranceExpiry.label')}
              />
            )}
          </form.Field>

          <form.Field name="inspectionExpiry">
            {(field) => (
              <DateField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.inspectionExpiry.label')}
              />
            )}
          </form.Field>

          <form.Field name="tachographInspectionExpiry">
            {(field) => (
              <DateField
                field={field}
                layout="inline"
                labelWidth={LABEL_W}
                label={t('vehicle.tachographInspectionExpiry.label')}
              />
            )}
          </form.Field>
        </div>
      </Panel>

      <Panel variant="filled" icon="user" title={t('vehicle.sections.drivers')}>
        <form.Field name="preferredDrivers">
          {(field) => (
            <DriverSelectorField
              field={field}
              mode="multi"
              layout="inline"
              labelWidth={LABEL_W}
              label={t('vehicle.drivers.label')}
              placeholder={t('vehicle.drivers.placeholder')}
              testId="vehicle-drivers"
            />
          )}
        </form.Field>
      </Panel>
    </div>
  );
}
